/**
 * E2E Encryption using Web Crypto API
 * ECDH key exchange + AES-GCM encryption
 */

const ALGO = { name: 'ECDH', namedCurve: 'P-256' };
const AES_ALGO = { name: 'AES-GCM', length: 256 };
const DB_NAME = 'veonfox_crypto';
const STORE_NAME = 'keys';

// IndexedDB helpers for persistent key storage
const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const dbGet = async (key) => {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
};

const dbSet = async (key, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Generate ECDH key pair
export const generateKeyPair = async (userId) => {
  const keyPair = await crypto.subtle.generateKey(ALGO, true, ['deriveKey']);
  // Export public key to share
  const publicKeyRaw = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  // Store private key in IndexedDB
  const privateKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
  const privateKeyName = userId ? `privateKey_${userId}` : 'privateKey';
  const publicKeyName = userId ? `publicKey_${userId}` : 'publicKey';
  await dbSet(privateKeyName, privateKeyJwk);
  await dbSet(publicKeyName, publicKeyRaw);
  return JSON.stringify(publicKeyRaw);
};

// Get stored key pair
export const getStoredPublicKey = async (userId) => {
  const publicKeyName = userId ? `publicKey_${userId}` : 'publicKey';
  const pub = await dbGet(publicKeyName);
  return pub ? JSON.stringify(pub) : null;
};

// Derive shared AES key from our private key and their public key
const deriveSharedKey = async (theirPublicKeyJwk, myUserId) => {
  const privateKeyName = myUserId ? `privateKey_${myUserId}` : 'privateKey';
  const privateKeyJwk = await dbGet(privateKeyName);
  if (!privateKeyJwk) throw new Error('No private key found');
  const privateKey = await crypto.subtle.importKey('jwk', privateKeyJwk, ALGO, false, ['deriveKey']);
  const theirKey = typeof theirPublicKeyJwk === 'string' ? JSON.parse(theirPublicKeyJwk) : theirPublicKeyJwk;
  const publicKey = await crypto.subtle.importKey('jwk', theirKey, ALGO, false, []);
  return crypto.subtle.deriveKey({ name: 'ECDH', public: publicKey }, privateKey, AES_ALGO, false, ['encrypt', 'decrypt']);
};

// Encrypt a message.
// FAIL-CLOSED: never silently fall back to plaintext. If encryption cannot happen
// (missing/invalid peer key, crypto error) this THROWS, and the caller must abort
// the send rather than leak the message (or, for media, the file key) in the clear.
export const encryptMessage = async (plaintext, theirPublicKeyJwk, myUserId) => {
  const sharedKey = await deriveSharedKey(theirPublicKeyJwk, myUserId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, sharedKey, encoded);
  // Combine IV + ciphertext and base64 encode
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return btoa(String.fromCharCode(...combined));
};

// Safety number (à la Signal): a deterministic fingerprint of BOTH public keys,
// order-independent, so both parties compute the SAME number. Comparing it
// out-of-band detects a man-in-the-middle / server key substitution.
export const getSafetyNumber = async (pubKeyA, pubKeyB) => {
  try {
    const norm = (k) => {
      const j = typeof k === 'string' ? JSON.parse(k) : k;
      return `${j.crv}:${j.x}:${j.y}`;
    };
    const parts = [norm(pubKeyA), norm(pubKeyB)].sort();
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join('|')));
    const bytes = new Uint8Array(digest);
    let num = '';
    for (let i = 0; i < 12; i++) num += (bytes[i] % 100000).toString().padStart(5, '0');
    return num.match(/.{1,5}/g).join(' ');
  } catch {
    return null;
  }
};

// Decrypt a message
export const decryptMessage = async (ciphertextB64, theirPublicKeyJwk, myUserId) => {
  try {
    const combined = Uint8Array.from(atob(ciphertextB64), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const sharedKey = await deriveSharedKey(theirPublicKeyJwk, myUserId);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, sharedKey, ciphertext);
    return new TextDecoder().decode(decrypted);
  } catch {
    return ciphertextB64; // Return as-is if decryption fails
  }
};

// Check if a message is encrypted (base64 encoded binary)
export const isEncrypted = (text) => {
  if (!text || text.length < 20) return false;
  try {
    const decoded = atob(text);
    return decoded.length > 12 && !/^[\x20-\x7E\s]+$/.test(decoded);
  } catch {
    return false;
  }
};

// --- E2E File Encryption ---

export const generateFileKey = async () => {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const exported = await crypto.subtle.exportKey('jwk', key);
  return { key, jwk: exported };
};

export const encryptFile = async (file) => {
  const { key, jwk } = await generateFileKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const arrayBuffer = await file.arrayBuffer();
  
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    arrayBuffer
  );
  
  const encryptedBlob = new Blob([encryptedBuffer], { type: 'application/octet-stream' });
  const ivBase64 = btoa(String.fromCharCode(...iv));
  
  return {
    encryptedBlob,
    symmetricKeyJwk: jwk,
    ivBase64,
    mimeType: file.type || 'application/octet-stream'
  };
};

export const decryptFile = async (encryptedBlob, symmetricKeyJwk, ivBase64, mimeType) => {
  const key = await crypto.subtle.importKey(
    'jwk',
    symmetricKeyJwk,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  
  const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
  const arrayBuffer = await encryptedBlob.arrayBuffer();
  
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    arrayBuffer
  );
  
  return new Blob([decryptedBuffer], { type: mimeType });
};
